/**
 * ステムミックス再生エンジン（Web Audio / React 非依存のシングルトン）
 *
 * ボーカルとインストを中間音量で混ぜるときだけ使う。
 * 2 つの HTMLAudioElement を並べる方式では、デコーダのバッファ差と独立したクロックのせいで
 * 数十 ms 単位のずれが出て歌が聴けたものにならないため、
 * 単一の AudioContext 上で「同じ start 時刻・同じ offset」の 2 音源として鳴らす。
 * これでサンプル単位の一致が保証され、音量をいくら動かしてもずれない。
 *
 * ただし AudioContext はバックグラウンドで suspend されるため、
 * この方式では iOS のバックグラウンド再生が続かない。
 * 音量が振り切っている組み合わせでは要素再生エンジンに任せる（music-playback.ts が判断する）。
 */

import { type MusicTrack, useMusicStore } from '@/store/useMusicStore'

import { peekSigned, resolveSigned, streamPath, withStem } from './music-signed-url'

type LoadedBuffers = { episodeId: number; vocals: AudioBuffer; inst: AudioBuffer }

/** start() を予約する際の余裕。0 だと環境によっては頭が欠ける */
const START_DELAY_SEC = 0.05

/** 音量変化のなめらかさ（秒）。クリックノイズを防ぐ */
const GAIN_RAMP_SEC = 0.01

/** ストアへ再生位置を書き戻す間隔（ms）。audio 要素の timeupdate と同程度にする */
const POSITION_INTERVAL_MS = 250

/** ボーカルとインストの長さのずれをどこまで許容するか（秒） */
const LENGTH_TOLERANCE_SEC = 0.05

let context: AudioContext | null = null
let vocalGain: GainNode | null = null
let instGain: GainNode | null = null
let vocalSource: AudioBufferSourceNode | null = null
let instSource: AudioBufferSourceNode | null = null
let buffers: LoadedBuffers | null = null

/** このエンジンが再生を担当しているか */
let mixActive = false

/** 音を鳴らす意図があるか。OS による中断と自分の一時停止を区別するために使う */
let playIntent = false

/** OS にバックグラウンドで止められた。前面に戻ったら再開を試みる */
let resumeOnForeground = false

/** source を start した時点の ctx.currentTime */
let startCtxTime = 0

/** start 時に指定した音源内オフセット（秒）。停止中はここが現在位置になる */
let startOffset = 0

/** source が start 済みか（suspend による一時停止中も true） */
let running = false

/** 読み込み要求の世代。曲送り連打で古い結果を捨てるために使う */
let loadGeneration = 0

/** source の世代。stop() が投げる onended を無視するために使う */
let sourceGeneration = 0

let positionTimer: ReturnType<typeof setInterval> | null = null
let unsubscribeStore: (() => void) | null = null

/** 音源を用意できなかったときに呼ぶ。切替の責務は music-playback.ts が持つ */
let onLoadFailure: (() => void) | null = null

export const setMixFailureHandler = (handler: (() => void) | null): void => {
  onLoadFailure = handler
}

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max)

const currentTrack = (): MusicTrack | undefined => {
  const { queue, currentIndex } = useMusicStore.getState()
  return queue.at(currentIndex)
}

const getContext = (): AudioContext => {
  if (context) return context

  context = new AudioContext()
  vocalGain = context.createGain()
  instGain = context.createGain()
  vocalGain.connect(context.destination)
  instGain.connect(context.destination)

  const { vocalVolume, instVolume } = useMusicStore.getState()
  vocalGain.gain.value = vocalVolume
  instGain.gain.value = instVolume

  context.addEventListener('statechange', handleStateChange)
  return context
}

const applyGain = (gain: GainNode | null, value: number): void => {
  if (!gain || !context) return
  gain.gain.setTargetAtTime(clamp(value, 0, 1), context.currentTime, GAIN_RAMP_SEC)
}

// ============================================================
// 再生位置
// ============================================================

/** 現在の再生位置（秒）。suspend 中は ctx.currentTime が止まるので自動的に固定される */
export const getMixPosition = (): number => {
  if (!context || !buffers) return startOffset
  if (!running) return startOffset
  const elapsed = Math.max(context.currentTime - startCtxTime, 0)
  return Math.min(startOffset + elapsed, buffers.vocals.duration)
}

const pushPosition = (): void => {
  useMusicStore.getState().setCurrentTime(getMixPosition())
}

const startPositionTimer = (): void => {
  if (positionTimer !== null) return
  positionTimer = setInterval(pushPosition, POSITION_INTERVAL_MS)
}

const stopPositionTimer = (): void => {
  if (positionTimer === null) return
  clearInterval(positionTimer)
  positionTimer = null
}

// ============================================================
// 音源の読み込み
// ============================================================

const decodeStem = async (ctx: AudioContext, url: string): Promise<AudioBuffer> => {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`ステムの取得に失敗しました (${res.status})`)
  const data = await res.arrayBuffer()
  return ctx.decodeAudioData(data)
}

const loadBuffers = async (episodeId: number): Promise<LoadedBuffers | null> => {
  const path = streamPath(episodeId)
  const url = peekSigned(path) ?? (await resolveSigned(path))
  if (!url) return null

  const ctx = getContext()
  try {
    // 2 本まとめて待つ。片方だけ先に鳴らすことはしない
    const [vocals, inst] = await Promise.all([
      decodeStem(ctx, withStem(url, 'vocals')),
      decodeStem(ctx, withStem(url, 'no_vocals')),
    ])
    if (Math.abs(vocals.duration - inst.duration) > LENGTH_TOLERANCE_SEC) {
      console.warn('[MusicStem] ボーカルとインストの長さが一致しません:', vocals.duration, inst.duration)
    }
    return { episodeId, vocals, inst }
  } catch (err) {
    console.error('[MusicStem] ステムの読み込みに失敗しました:', err)
    return null
  }
}

// ============================================================
// 再生制御
// ============================================================

const stopSources = (): void => {
  // 世代を進めて、stop() が発火させる onended を無視させる
  sourceGeneration += 1
  for (const source of [vocalSource, instSource]) {
    if (!source) continue
    source.onended = null
    try {
      source.stop()
    } catch {
      // 未開始の source は無視してよい
    }
    source.disconnect()
  }
  vocalSource = null
  instSource = null
  running = false
}

const handleEnded = (): void => {
  running = false
  stopPositionTimer()

  const store = useMusicStore.getState()
  if (store.repeatMode === 'one') {
    startSources(0)
    store.setCurrentTime(0)
    startPositionTimer()
    return
  }
  // 次曲の読み込みは購読側で行う。キュー末尾なら isPlaying が false になる
  store.playNext()
}

/** ボーカルとインストを同じ時刻・同じオフセットで鳴らし始める（サンプル単位で一致する） */
function startSources(offset: number): void {
  if (!buffers) return
  const ctx = getContext()
  if (!vocalGain || !instGain) return

  stopSources()

  const clamped = clamp(offset, 0, buffers.vocals.duration)
  const when = ctx.currentTime + START_DELAY_SEC
  const generation = ++sourceGeneration

  vocalSource = ctx.createBufferSource()
  vocalSource.buffer = buffers.vocals
  vocalSource.connect(vocalGain)

  instSource = ctx.createBufferSource()
  instSource.buffer = buffers.inst
  instSource.connect(instGain)

  vocalSource.onended = () => {
    if (generation !== sourceGeneration || !mixActive) return
    handleEnded()
  }

  vocalSource.start(when, clamped)
  instSource.start(when, clamped)

  startCtxTime = when
  startOffset = clamped
  running = true
}

const play = async (): Promise<void> => {
  if (!mixActive) return
  if (!buffers) {
    await loadAndStart(startOffset)
    return
  }

  playIntent = true
  resumeOnForeground = false
  if (!running) startSources(startOffset)

  try {
    await getContext().resume()
  } catch (err) {
    console.warn('[MusicStem] 再生を開始できませんでした:', err)
    playIntent = false
    useMusicStore.getState().setPlaying(false)
    return
  }
  startPositionTimer()
}

const pause = (): void => {
  if (!playIntent) return
  // suspend で止めれば source の予約は生きたままなので、再開しても 2 音源のずれは生じない
  playIntent = false
  stopPositionTimer()
  void context?.suspend()
}

const seek = (position: number): void => {
  if (!buffers) {
    startOffset = Math.max(position, 0)
    return
  }
  const clamped = clamp(position, 0, buffers.vocals.duration)
  if (!running) {
    startOffset = clamped
    return
  }
  startSources(clamped)
}

/** 現在のトラックを読み込んで指定位置から鳴らす。準備できたかを返す */
async function loadAndStart(position: number): Promise<boolean> {
  const generation = ++loadGeneration
  stopSources()
  stopPositionTimer()

  const track = currentTrack()
  if (!track) {
    buffers = null
    startOffset = 0
    return false
  }

  if (buffers?.episodeId !== track.episodeId) {
    // 前の曲のバッファは数十MB規模になるので、読み込み前に手放す
    buffers = null
    const loaded = await loadBuffers(track.episodeId)
    if (generation !== loadGeneration || !mixActive) return false
    if (!loaded) {
      useMusicStore.getState().setPlaying(false)
      onLoadFailure?.()
      return false
    }
    buffers = loaded
  }

  const store = useMusicStore.getState()
  store.setDuration(buffers.vocals.duration)
  startOffset = clamp(position, 0, buffers.vocals.duration)
  store.setCurrentTime(startOffset)

  if (store.isPlaying) await play()
  return true
}

// ============================================================
// 中断への対応
// ============================================================

function handleStateChange(): void {
  if (!mixActive || !context) return
  if (context.state === 'running') return
  // 自分で suspend したときは playIntent を先に落としてあるので、ここには来ない
  if (!playIntent) return

  // バックグラウンド遷移などで OS に止められた。UI と実際の状態を合わせる
  playIntent = false
  resumeOnForeground = true
  stopPositionTimer()
  useMusicStore.getState().setPlaying(false)
}

const handleVisibilityChange = (): void => {
  if (!mixActive || !resumeOnForeground) return
  if (document.visibilityState !== 'visible') return

  resumeOnForeground = false
  void context
    ?.resume()
    .then(() => {
      if (!mixActive || context?.state !== 'running') return
      playIntent = true
      startPositionTimer()
      useMusicStore.getState().setPlaying(true)
    })
    .catch(() => {
      // ユーザー操作なしでは再開できない環境。再生ボタンで復帰できる
    })
}

/** 動画など別のメディアが鳴り始めたら音楽を止める（要素再生エンジンと同じ扱い） */
const handleForeignPlay = (event: Event): void => {
  if (!mixActive || !playIntent) return
  const target = event.target
  if (!(target instanceof HTMLMediaElement)) return
  if (target.muted || target.volume === 0) return

  useMusicStore.getState().setPlaying(false)
}

// ============================================================
// ストアの購読
// ============================================================

const subscribeStore = (): void => {
  unsubscribeStore = useMusicStore.subscribe((state, prev) => {
    if (!mixActive) return

    const track = state.queue.at(state.currentIndex)
    const prevTrack = prev.queue.at(prev.currentIndex)
    if (track?.episodeId !== prevTrack?.episodeId) {
      void loadAndStart(0)
      return
    }

    if (state.vocalVolume !== prev.vocalVolume) applyGain(vocalGain, state.vocalVolume)
    if (state.instVolume !== prev.instVolume) applyGain(instGain, state.instVolume)

    if (state.seekVersion !== prev.seekVersion) seek(state.currentTime)

    if (state.isPlaying === prev.isPlaying) return
    if (state.isPlaying) void play()
    else pause()
  })
}

// ============================================================
// 有効・無効の切り替え
// ============================================================

/** このエンジンに再生を任せる。指定位置から読み込み直す（ストアが再生中ならそのまま鳴り始める） */
export const activateMixEngine = async (position: number): Promise<boolean> => {
  mixActive = true
  if (!unsubscribeStore) subscribeStore()
  document.addEventListener('visibilitychange', handleVisibilityChange)
  document.addEventListener('play', handleForeignPlay, true)

  const { vocalVolume, instVolume } = useMusicStore.getState()
  getContext()
  applyGain(vocalGain, vocalVolume)
  applyGain(instGain, instVolume)

  return loadAndStart(position)
}

/**
 * 再生を手放して停止する。
 * 切替中に UI が点滅しないよう、ストアの isPlaying は意図的に触らない。
 */
export const deactivateMixEngine = (): number => {
  const position = getMixPosition()
  if (!mixActive) return position

  mixActive = false
  playIntent = false
  resumeOnForeground = false
  loadGeneration += 1
  stopPositionTimer()
  stopSources()
  startOffset = position
  // バッファは数十MB規模なので、使わないなら即座に解放する
  buffers = null
  void context?.suspend()

  document.removeEventListener('visibilitychange', handleVisibilityChange)
  document.removeEventListener('play', handleForeignPlay, true)
  return position
}

/** エンジンを破棄する（`/music` 配下から離れたときと HMR 時に呼ぶ） */
export const disposeMixEngine = (): void => {
  unsubscribeStore?.()
  unsubscribeStore = null
  deactivateMixEngine()

  if (context) {
    context.removeEventListener('statechange', handleStateChange)
    void context.close()
    context = null
  }
  vocalGain = null
  instGain = null
  startOffset = 0
}
